/**
 * Custom schema for panels header
 * Displays strength in a framed box (material subtitle omitted on PDF)
 */

import { PDF_TITLE_SIZE } from "./template";

/**
 * Draw the panels header with framed strength
 */
async function drawPanelsHeader(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: {
    position: { x: number; y: number };
    width: number;
    height: number;
  };
  value: string;
}): Promise<void> {
  const { rgb, StandardFonts } = await import("pdf-lib");
  const { value } = arg;

  // Value format remains "strength|material" for pdfme; PDF draws strength only
  const parts = (value || "").split("|");
  const strength = parts[0] || "";

  if (!strength) {
    return;
  }

  const { x, y } = arg.schema.position;
  const { width } = arg.schema;
  const pageHeight = arg.page.getHeight();

  // Convert mm to points
  const MM_TO_PT = 2.83465;
  const startX = x * MM_TO_PT;
  const startY = pageHeight - y * MM_TO_PT; // Top of schema area (PDF coordinates)

  const fontBold = await arg.pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const headerFontSize = PDF_TITLE_SIZE; // Use central font size definition

  const strengthText = strength.trim();
  const textWidth = fontBold.widthOfTextAtSize(strengthText, headerFontSize);
  const frameWidth = textWidth + 8; // padding
  const frameHeight = headerFontSize + 6;
  const frameX = startX + (width * MM_TO_PT - frameWidth) / 2;

  const topPadding = 2; // points
  const frameTopY = startY - topPadding;
  const frameBottomY = frameTopY - frameHeight;

  arg.page.drawRectangle({
    x: frameX,
    y: frameBottomY,
    width: frameWidth,
    height: frameHeight,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
    color: undefined,
  });

  arg.page.drawText(strengthText, {
    x: frameX + 4,
    y: frameBottomY + frameHeight / 2 - headerFontSize / 3,
    size: headerFontSize,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
}

export const panelsHeaderSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "#f5f5f5";
    div.style.display = "flex";
    div.style.flexDirection = "column";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.fontSize = "10px";
    div.style.color = "#666";
    const parts = (arg.value || "").split("|");
    div.innerHTML = `<div style="border: 1px solid #000; padding: 2px 4px;">${parts[0] || ""}</div>`;
    arg.rootElement.appendChild(div);
  },

  pdf: drawPanelsHeader,

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "panelsHeader",
      type: "panelsHeader",
      position: { x: 0, y: 0 },
      width: 95,
      height: 11,
    },
    defaultValue: "",
  },
};

export default panelsHeaderSchema;
